/**
 * The analytics a chat turn emits.
 *
 * Extracted from `useAgentChat.ts` when that file crossed the repo's 1000-line
 * ceiling. Pure and React-free, so the three-way branch below can be tested
 * without mounting a hook.
 */
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { safeAnalytics } from '../../utils/analytics';

/**
 * The one analytics event this turn earns, by what kind of turn it is.
 *
 * Extracted when the choice reply made this a three-way branch and pushed
 * `sendMessage` past the complexity ceiling.
 *
 * A HITL reply is a button press, not a message the researcher composed.
 * Counting either as CHAT_MESSAGE_SENT would inflate the core funnel with
 * clicks, so each gets its own event.
 *
 * Note what is NOT sent: the choice's `value`. It is an entity id, which is
 * precisely what does not belong in a property bag (rules/analytics.md).
 */
export function trackTurnSent(args: {
  readonly sessionId: string;
  readonly confirmation?: { readonly callId: string; readonly confirmed: boolean };
  readonly choice?: { readonly callId: string; readonly value: string };
  readonly contextType?: string;
  readonly contextCount: number;
  readonly fileCount: number;
}): void {
  const { sessionId, confirmation, choice, contextType, contextCount, fileCount } = args;
  if (confirmation) {
    safeAnalytics.track(ANALYTICS_EVENTS.CHAT_TOOL_CONFIRMATION_ANSWERED, {
      session_id: sessionId,
      confirmed: confirmation.confirmed,
    });
    return;
  }
  if (choice) {
    safeAnalytics.track(ANALYTICS_EVENTS.CHAT_TOOL_CHOICE_ANSWERED, { session_id: sessionId });
    return;
  }
  safeAnalytics.track(ANALYTICS_EVENTS.CHAT_MESSAGE_SENT, {
    session_id: sessionId,
    context_type: contextType,
    context_count: contextCount,
    has_files: fileCount > 0,
    file_count: fileCount,
  });
}
